import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export type MultiStatus = 'won' | 'lost' | 'void' | 'pending';

export interface MultiLeg {
  leg_no: number;
  race_id: string;
  race_number: number | null;
  track_name: string | null;
  tab_number: number;
  horse_name: string;
  /** 1 = win, N = finish top N */
  finish_within: number;
  field_size: number;
  chance: number | string;
  est_price: number | string;
  status: MultiStatus;
  finish_position: number | null;
}

export interface Multi {
  id: number;
  slot: number;
  kind: 'banker' | 'strong' | 'mid' | 'value' | 'long' | 'value_pick';
  name: string;
  rationale: string | null;
  leg_count: number;
  chance: number | string;
  est_price: number | string;
  settled_price: number | string | null;
  status: MultiStatus;
  model: string | null;
  built_at: string;
  legs: MultiLeg[];
}

export function useMultis(date: string) {
  return useQuery({
    queryKey: ['multis', date],
    queryFn: async () => (await apiClient<{ date: string; multis: Multi[] }>(`/multis?date=${encodeURIComponent(date)}`)).multis,
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
}

export type ValueRadarGroup = 'avoid' | 'keep_race' | 'keep_place' | 'keep';

/** One Value Radar horse on the day's board (build-your-own list). */
export interface ValueRadarHorse {
  id: number;
  race_id: string;
  runner_id: string;
  tab_number: number | null;
  horse_name: string;
  track_name: string | null;
  race_number: number | null;
  field_size: number | null;
  max_n: number | null;
  vr_group: ValueRadarGroup;
  reason: string | null;
  proposed_n: number | null;
  chance: number | string | null;
  est_price: number | string | null;
  chance_top3: number | string | null;
  chance_top4: number | string | null;
  chance_top5: number | string | null;
  tip_odds: number | string | null;
  bet_type: string | null;
  pick: string | null;
  finish_position: number | null;
  status: MultiStatus;
}

export interface ValueRadarRecordRow {
  vr_group: ValueRadarGroup;
  runners: number;
  top3_pct: number;
  top4_pct: number;
  top5_pct: number;
  proposed_hit_pct: number;
  proposed_expected_pct: number;
}

export function useValueRadarBoard(date: string) {
  return useQuery({
    queryKey: ['value-radar-board', date],
    queryFn: async () => (await apiClient<{ horses: ValueRadarHorse[] }>(`/multis/value-radar?date=${encodeURIComponent(date)}`)).horses,
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
}

export function useValueRadarRecord(days = 30) {
  return useQuery({
    queryKey: ['value-radar-record', days],
    queryFn: async () => (await apiClient<{ groups: ValueRadarRecordRow[] }>(`/multis/value-radar/record?days=${days}`)).groups,
  });
}

/** One leg of a multi the chat built for a member, with how it went. */
export interface ChatMultiLeg {
  raceId: string | null;
  runnerId: string | null;
  trackName: string | null;
  raceNumber: number | null;
  jumpTime: string | null;
  tabNumber: number;
  horseName: string;
  /** 1 = win, N = finish top N */
  finishWithin: number;
  position: number | null;
  status: MultiStatus;
}

/** A multi the chat (Ask Tippa) built for a member at the price they asked for. */
export interface ChatMulti {
  id: number;
  conversationId: number;
  createdAt: string;
  askedPrice: number | null;
  /** "Spin another": built after another multi at the same price. */
  spin: boolean;
  estPrice: number;
  chance: number;
  status: MultiStatus;
  settledPrice: number | null;
  legs: ChatMultiLeg[];
}

export interface ChatMultiSummaryRow {
  band: string;
  multis: number;
  asks: number;
  settled: number;
  won: number;
  expected: number;
  profit: number | null;
}

export function useChatMultis(date: string) {
  return useQuery({
    queryKey: ['chat-multis', date],
    queryFn: async () => apiClient<{ multis: ChatMulti[]; summary: ChatMultiSummaryRow[] }>(`/multis/chat?date=${encodeURIComponent(date)}`),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
}

export function useChatMultisRecord(days = 30) {
  return useQuery({
    queryKey: ['chat-multis-record', days],
    queryFn: async () => (await apiClient<{ summary: ChatMultiSummaryRow[] }>(`/multis/chat/record?days=${days}`)).summary,
  });
}
